import type { UploadRow } from '@/pages/term-base/_components/ingest-panel'

/**
 * In-flight uploads, held outside React.
 *
 * They used to live in component state inside the documents view, which meant
 * that leaving that view — to another tab, or just switching language, since
 * that rewrites the URL — unmounted the component, and its cleanup aborted the
 * transfer. The queue looked lost because it *was*: the page owned it.
 *
 * A module-level store outlives any component. The upload loop runs here, not
 * in a hook, so navigating away is now invisible to it; components subscribe to
 * watch, and unsubscribing stops nothing. Only an explicit cancel aborts.
 *
 * This covers everything except a genuine page load. `File` objects cannot
 * survive one — the bytes live in the page — so a reload still costs whatever
 * had not been transferred yet. `guardUnload` makes that a deliberate choice
 * rather than an accident.
 */

type Listener = () => void

const rowsByCollection = new Map<number, UploadRow[]>()
const controllers = new Map<number, AbortController>()
const listeners = new Set<Listener>()

const EMPTY: UploadRow[] = []

function emit() {
  for (const l of listeners) l()
}

export function subscribe(l: Listener): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function getUploads(collectionId: number | null): UploadRow[] {
  if (collectionId === null) return EMPTY
  return rowsByCollection.get(collectionId) ?? EMPTY
}

export function setUploads(
  collectionId: number,
  update: UploadRow[] | ((cur: UploadRow[]) => UploadRow[]),
) {
  const cur = rowsByCollection.get(collectionId) ?? EMPTY
  const next = typeof update === 'function' ? update(cur) : update
  if (next.length) rowsByCollection.set(collectionId, next)
  else rowsByCollection.delete(collectionId)
  emit()
}

export function clearUploads(collectionId: number) {
  rowsByCollection.delete(collectionId)
  emit()
}

export function beginRun(collectionId: number): AbortController {
  controllers.get(collectionId)?.abort()
  const c = new AbortController()
  controllers.set(collectionId, c)
  return c
}

export function endRun(collectionId: number) {
  controllers.delete(collectionId)
}

/** Explicit cancel — the only thing that stops a transfer. */
export function abortRun(collectionId: number) {
  controllers.get(collectionId)?.abort()
  controllers.delete(collectionId)
}

export function isSending(collectionId: number | null): boolean {
  if (collectionId === null) return false
  return (rowsByCollection.get(collectionId) ?? EMPTY).some(
    (u) => u.status === 'checking' || u.status === 'waiting' || u.status === 'sending',
  )
}

/** Any collection at all — the unload guard is not per-view. */
function anySending(): boolean {
  for (const rows of rowsByCollection.values()) {
    if (rows.some((u) => u.status === 'checking' || u.status === 'waiting' || u.status === 'sending')) {
      return true
    }
  }
  return false
}

/**
 * Ask before a reload discards files that have not been sent.
 *
 * Browsers ignore the message and show their own wording, so there is nothing
 * to translate; the only thing under our control is whether the prompt appears.
 */
export function guardUnload() {
  const onBeforeUnload = (e: BeforeUnloadEvent) => {
    if (!anySending()) return
    e.preventDefault()
    // Legacy browsers key off a non-empty returnValue.
    e.returnValue = ''
  }
  window.addEventListener('beforeunload', onBeforeUnload)
  return () => window.removeEventListener('beforeunload', onBeforeUnload)
}
